import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { toast } from 'sonner';
import { userAPI } from '../../services/api';
import { DollarSign, Coins } from 'lucide-react';

const SUPPORTED_CURRENCIES = [
  {
    code: 'USD',
    name: 'US Dollar',
    icon: DollarSign,
    description: 'Widely accepted, stable',
    flag: '🇺🇸'
  },
  {
    code: 'ZAR',
    name: 'South African Rand',
    icon: Coins,
    description: 'Local currency',
    flag: '🇿🇦'
  }
];

export const CurrencySelectionModal = ({ isOpen, onClose, onSuccess }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!selectedCurrency) {
      toast.error('Please select a currency');
      return;
    }

    setLoading(true);
    try {
      await userAPI.updateProfile({ default_currency: selectedCurrency });
      toast.success(`Default currency set to ${selectedCurrency}`);
      
      if (onSuccess) {
        onSuccess(selectedCurrency);
      }
      
      onClose();
      
      // Reload page to refresh user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set currency');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Choose Your Default Currency</DialogTitle>
          <DialogDescription>
            Select the currency you'll use for your wallet and transactions. You can change this later in Settings.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <RadioGroup value={selectedCurrency} onValueChange={setSelectedCurrency}>
            {SUPPORTED_CURRENCIES.map((currency) => {
              const Icon = currency.icon;
              return (
                <label
                  key={currency.code}
                  className={`
                    flex items-start space-x-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${selectedCurrency === currency.code 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                    }
                  `}
                >
                  <RadioGroupItem value={currency.code} id={currency.code} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{currency.flag}</span>
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{currency.code} - {currency.name}</div>
                        <div className="text-sm text-muted-foreground">{currency.description}</div>
                      </div>
                    </div>
                  </div>
                </label>
              );
            })}
          </RadioGroup>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSubmit}
            disabled={!selectedCurrency || loading}
            className="flex-1"
          >
            {loading ? 'Setting...' : 'Confirm & Continue'}
          </Button>
        </div>

        <p className="text-xs text-center text-muted-foreground">
          This choice helps us show you accurate balances and pricing
        </p>
      </DialogContent>
    </Dialog>
  );
};
